import moment from 'moment';
import '../components/Tab.css';
import '../components/Calendar.css';
import Box from '../components/Box';
import { translate } from '../locale';
import Text from '../components/Text';
import Button from '../components/Button';
import useCourses from '../hooks/useCourses';
import { useBooking } from '../BookingProvider';
import { useHistory } from '../hooks/useParams';
import FullPageSpinner from '../components/FullPageSpinner';
import React, { useEffect, useMemo, useState } from 'react';

export default function VkuCalendarPage() {
  const history = useHistory();
  const { addCourse, locale, courses: selectedCourses } = useBooking();
  const [isLoadingCourses, courses] = useCourses("VKU");

  const [ranges, setRanges] = useState<Range[]>([]);
  const [selectedRange, setSelectedRange] = useState<Range>();
  const selectedCourseParts = useMemo<number[]>(() => selectedCourses.map(c => c.part), [selectedCourses]);
  const [mobileView, setMobileView] = useState(window.innerWidth < 860);

  useEffect(() => {
    if (!selectedRange && ranges.length > 0) {
      setSelectedRange(ranges[0]);
    }
  }, [ranges, selectedRange]);

  useEffect(() => {
    const ranges: Range[] = [];

    if (courses) {
      for (const course of courses) {
        Object.assign(course, {
          bookingsCount: course.freePlaces,
          dateRange1: {
            start1: course.start1,
            end1: course.end1
          }
        });

        const start1Date = moment(course.start1);

        const month = start1Date.month();
        const year = start1Date.year();

        let range = ranges.find(range => range.month === month && range.year === year);

        if (!range) {
          range = {
            year,
            month,
            coursesByDay: new Map()
          };

          ranges.push(range);
        }

        const day = start1Date.date();
        let courses = range.coursesByDay.get(day);
        if (!courses) {
          courses = [];
          range.coursesByDay.set(day, courses);
        }

        courses.push(course);
      }
    }

    setRanges(ranges);
  }, [courses]);

  useEffect(() => {
    function handleResize() {
      setMobileView(window.innerWidth < 860);
    }
    window.addEventListener("resize", handleResize);
    handleResize();
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  function goToBooking() {
    history.push('/vku/booking');
  }

  function getDaysArrayByMonth() {
    if (!selectedRange) return [];

    const month = moment().year(selectedRange.year).month(selectedRange.month).startOf('month');

    var daysInMonth = month.daysInMonth();
    var arrDays: moment.Moment[] = [];

    for (let i = 0; i < daysInMonth; i++) {
      const current = moment(month).date(i + 1);
      if (current.isoWeekday() === 7) continue;
      arrDays.push(current);
    }

    return arrDays;
  }

  function handleCourseClick(course: any) {
    if (selectedCourses.length === 0 && course.part !== 1) {
      if (!window.confirm(translate(locale, 'infoMessage', 'vku1Completed'))) return;
    }

    if (getCourseItemClass(course) === 'course-item-disabled') return;

    addCourse(course);
  }

  function getCourseItemClass(course: any) {
    if (course.freePlaces === 0) return 'course-item-disabled';

    if (selectedCourses.length > 0) {
      const provArray = selectedCourses.concat();

      const courseWithSamePartIndex = selectedCourses.findIndex(c => c.part === course.part);
      if (courseWithSamePartIndex !== -1) provArray.splice(courseWithSamePartIndex, 1);

      provArray.push(course);

      const part1Course = provArray.find(x => x.part === 1);

      if (part1Course) {
        const [firstCourse] = provArray.sort((a, b) => moment(a.dateRange1.start1).valueOf() - moment(b.dateRange1.start1).valueOf());
        if (firstCourse.part !== 1) {
          return 'course-item-disabled';
        }
      }
    }

    if (selectedCourses.some(c => c._id === course._id)) return 'course-item-selected';
    if (selectedCourseParts.includes(course.part)) return 'course-item-hidden';

    return '';
  }

  const priceNoteTitleHeight = useMemo(
    () => mobileView ? 95 : 75,
    [mobileView]
  );

  if (isLoadingCourses) return <FullPageSpinner />
  if (!courses) return null;
  if (!selectedRange) return null;

  const dates = getDaysArrayByMonth();

  return (
    <Box>
      <Box style={{ position: 'fixed', width: '100%', backgroundColor: '#fff', 'zIndex': 9999 }}>
        <Box flex alignItems='center' style={{ height: 64, paddingLeft: 16 }}>
          <a style={{ display: 'flex', justifyContent: 'left' }} href='https://www.verkehrsschule.ch'>
            <img style={{ maxWidth: 250 }} alt='logo' src='https://www.verkehrsschule.ch/wp-content/uploads/2020/03/Verkehrsschule_Zug_Logo.png' />
          </a>
        </Box>
        <Box
          direction='column'
          paddingHorizontal={16}
          paddingVertical={8}
          style={{
            height: priceNoteTitleHeight + "px",
            overflow: "auto",
            fontSize: 13
          }}
        >
          <Text>
            <b>{translate(locale, 'vkuPriceTitle', 'bold')}</b> {translate(locale, 'vkuPriceTitle', 'note')}
          </Text>
          <Text>
            {translate(locale, 'vkuNote', 'note')}
          </Text>
        </Box>
        <Box direction='row' style={{ overflowX: 'auto' }}>
          {
            ranges.map(
              (range, index) =>
                <Box
                  key={index}
                  style={{ 'minWidth': '90px' }}
                  className={(range === selectedRange) ? 'calendar-month--selected' : ''}
                  padding={16}
                  background='white'
                  onClick={() => {
                    setSelectedRange(range);
                    window.scrollTo(0, 0);
                  }}
                  direction='column'
                  alignItems='center'
                >
                  <Text weight={'500'}>{moment().month(range.month).format('MMM')}</Text>
                  <Text size={10}>{range.year}</Text>
                </Box>
            )
          }
        </Box>
        <Box className="day-of-week">
          <Box>{translate(locale, 'daysShort', 'monday')}</Box>
          <Box>{translate(locale, 'daysShort', 'tuesday')}</Box>
          <Box>{translate(locale, 'daysShort', 'wednesday')}</Box>
          <Box>{translate(locale, 'daysShort', 'thursday')}</Box>
          <Box>{translate(locale, 'daysShort', 'friday')}</Box>
          <Box>{translate(locale, 'daysShort', 'saturday')}</Box>
        </Box>
      </Box>

      <Box
        className="date-grid"
        style={{
          paddingTop: 174 + priceNoteTitleHeight,
          paddingBottom: 100
        }}
      >
        {
          [
            ...Array(
              dates[0].isoWeekday() -
              1
            )
          ].map((value, index) =>
            <Box
              key={index}
              className='item'
            />
          )
        }
        {
          dates.map((date, dateIndex) =>
            <Box
              key={dateIndex}
              className='item'
              padding={1}
            >
              <Box padding={4}>
                <Text size={16} weight={'400'} color={'black'}>{date.date()}</Text>
              </Box>
              {
                (
                  selectedRange.coursesByDay
                    .get(date.date()) ||
                  []
                )
                  .sort(
                    (a, b) =>
                      moment(a.dateRange1.start1).valueOf() -
                      moment(b.dateRange1.start1).valueOf()
                  )
                  .map((course, index) =>
                    <Box
                      key={index}
                      cursor='pointer'
                      className={`course-item ${getCourseItemClass(course)}`}
                      background={'grey'}
                      direction='column'
                      onClick={() => handleCourseClick(course)}
                      flex
                    >
                      <Box direction='row'>
                        <Box width='flex'>
                          <Text weight={'500'} color={colorByPart[course.part]} size={13}>{course.type} {course.part}</Text>
                        </Box>
                      </Box>
                      <Text size={13}>{moment.utc(course.dateRange1.start1, 'YYYY-MM-DD HH:mm').format('HH:mm')}{!mobileView ? ` - ${moment.utc(course.dateRange1.end1, 'YYYY-MM-DD HH:mm').format('HH:mm')}` : ''}</Text>
                      <Text size={13}>{course.bookingsCount} {translate(locale, 'infoMessage', mobileView ? 'courseLeft' : (course.freePlaces === 1 ? 'coursesFree_singular' : 'coursesFree'))}</Text>
                      <Text color={course.language === 'en' ? 'red' : 'black'} size={13}>{translate(locale, mobileView ? 'language' : 'language_long', course.language || 'de')}</Text>
                    </Box>
                  )
              }
            </Box>
          )
        }
        {
          [
            ...Array(
              7 -
              dates[dates.length - 1].isoWeekday() -
              1
            )
          ]
            .map((value, index) =>
              <Box
                key={index}
                className='item'
              />
            )
        }
      </Box>
      <Box
        paddingHorizontal={16}
        alignItems='center'
        style={{
          display: selectedCourses.length > 0 ? '' : 'none',
          position: 'fixed',
          width: '100%',
          bottom: 0,
          height: '100px',
          backgroundColor: '#E31B1F'
        }}
      >
        <Box width='flex'>
          {
            selectedCourses.length < 4 ?
              <Text color='white'>Wählen Sie einen weiteren Kurs aus oder drücken Sie auf Anmeldung</Text> :
              <Text color='white'>Drücken Sie auf Anmeldung sich für die Kurse anzumelden</Text>
          }
        </Box>
        <Box paddingLeft={4}>
          <Button onClick={goToBooking} primary={true} label='Anmeldung'></Button>
        </Box>
      </Box>
    </Box>
  );
}

const colorByPart = {
  1: 'blue',
  2: 'yellow',
  3: 'green',
  4: 'orange',
}

type Range = {
  month: number;
  year: number;
  coursesByDay: Map<number, any[]>;
}